import { DataGridRef, Spinner } from '@cimplico/components';
import { useRef, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useCreateDocument } from '../../queries/use-create-document';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import { selector, _act } from '../../slices/filesSlice';
import { FileRecordNew, FileRecordTypeEnum, FileStatusEnum } from '../../types/file-record';
import { OtherSchedule } from '../../types/other-schedule';
import { ReconciliationWorksheet } from '../../types/worksheet';
import { toastNotification } from '../../components/toast/Toast';
import { FYI_URL } from '../../config';
import SecondaryPageActions from '../../components/secondary-page-actions/SecondaryPageActions';
import ClipboardImageUploadConfirm from './ConfirmationModals/ClipboardImageUploadConfirm';
import DocumentClearCheckOutConfirm from './ConfirmationModals/DocumentClearCheckOutConfirm';
import DocumentDownloadConfirm from './ConfirmationModals/DocumentDownloadConfirm';
import DocumentReplaceConfirm from './ConfirmationModals/DocumentReplaceConfirm';
import FileDuplicateConfirm from './ConfirmationModals/FileDuplicateConfirm';
import FileRecordRenameConfirm from './ConfirmationModals/FileRecordRenameConfirm';
import LinkReplaceConfirm from './ConfirmationModals/LinkReplaceConfirm';
import MakeReferenceOriginalConfirm from './ConfirmationModals/MakeReferenceOriginalConfirm';
import RecordDeleteConfirm from './ConfirmationModals/RecordDeleteConfirm';
import FileInput from './FileInput';
import FileRecordDetails from './FileRecordDetails';
import FilesPagePrimaryActions from './FilesPagePrimaryActions';
import MoveFileWindow from './MoveFileWindow';
import NewLinkDialog from './NewLinkDialog';
import Dropzone from '../../components/dropzone/Dropzone';
import queryClient from '../../query-client';
import FilesTable from './FilesTable';
import DocumentBrowser from '../../components/document-browser/DocumentBrowser';
import PageTitle from '../../components/PageTitle';
import NewReferencesWindow from './NewReferencesWindow';
import { useRefreshFYIDocuments } from '../../queries/use-refresh-fyi-documents';
import { getRedirectPath, hasWriteAccess, isFolderType } from './file-utils';
import { useUserContext } from '../../providers/UserProvider';
import { intercomEvents } from '../../intercom-events';
import { useIntercom } from 'react-use-intercom';

type FilesSectionProps = {
    title?: string;
    startingFolderId: string;
    workpaperId?: string;
    readOnly?: boolean;
    overlayInfoPanel?: boolean;
    useSmallStyling?: boolean;
    worksheet?: ReconciliationWorksheet;
    otherSchedule?: OtherSchedule;
    entityId?: string;
};

function FilesSection({
    title,
    startingFolderId,
    workpaperId,
    entityId,
    readOnly = false,
    overlayInfoPanel = false,
    useSmallStyling = false,
    worksheet,
    otherSchedule,
}: FilesSectionProps): JSX.Element {
    const { trackEvent } = useIntercom();
    const { id: firmId } = useFirmContext();

    const navigate = useNavigate();
    const uploadRef = useRef<HTMLInputElement | null>(null);
    const replaceRef = useRef<HTMLInputElement | null>(null);

    const [params, setParams] = useSearchParams();
    const [currentFolderId, setCurrentFolderId] = useState(params.get('folder') || startingFolderId || workpaperId || firmId);
    const [activeFileId, setActiveFileId] = useState<string | undefined>(params.get('activeFile') || undefined);
    const [uploading, setUploading] = useState(false);
    const [isClipboardImage, setIsClipboardImage] = useState(false);
    const [clipboardFile, setClipboardFile] = useState<File | undefined>();
    const [replacementDocument, setReplacementDocument] = useState<File | undefined>();
    const [filterQuery, setFilterQuery] = useState<string | undefined>();
    const [sortQuery, setSortQuery] = useState<string | undefined>();
    const [searchQuery, setSearchQuery] = useState<string | undefined>('');

    const dispatch = useDispatch();
    const { _action, records, currentPath } = useSelector(selector);
    const { shortId } = useFirmContext();
    const { isExternalUser } = useIsFirmRole();
    const { assignedWorkpapers } = useUserContext();

    const createDocumentQuery = useCreateDocument();

    const syncFYIQuery = useRefreshFYIDocuments();

    const gridRef = useRef<DataGridRef>() as React.MutableRefObject<DataGridRef>;
    const searchRef = useRef<HTMLInputElement>() as React.MutableRefObject<HTMLInputElement>;

    const fileLimit = 30;

    const canAction = currentPath && hasWriteAccess(isExternalUser, assignedWorkpapers, [currentPath]);
    const disablePrimaryButton =
        readOnly ||
        !currentFolderId ||
        currentFolderId === firmId ||
        Boolean(currentPath?.at(-1)?.provider) ||
        Boolean(currentPath?.at(-1)?.locked) ||
        !canAction;
    const disableMove = readOnly || records._all.every((r) => isFolderType(r.recordType));
    const disableDelete = readOnly || records.toDelete.length === 0;

    const handleFolderChange = useCallback(function handleFolderChange(id: string, activeFileId?: string): void {
        setCurrentFolderId(id);
        setActiveFileId(activeFileId);
        resetTableSelections();
        setSearchQuery('');
    }, []);

    useEffect(() => {
        setParams((prev) => {
            prev.set('folder', currentFolderId);
            return prev;
        });
    }, [currentFolderId]);

    useEffect(() => {
        // Temporary as state and url params can get out of sync. The timeout lets the state be set first when changing folders manually
        // TODO: Use url params as source of truth and remove state (WP-1347)
        setTimeout(() => {
            const folderParam = params.get('folder');
            if (typeof folderParam === 'string' && folderParam !== currentFolderId) {
                setCurrentFolderId(folderParam);
            }
        }, 0);
    }, [currentFolderId, params]);

    useEffect(() => {
        if (params.get('activeFile')) {
            setActiveFileId(params.get('activeFile') as string);
            setTimeout(() => {
                setParams((prev) => {
                    prev.delete('activeFile');
                    return prev;
                });
            });
        }
    }, [activeFileId]);

    function openFileInput(): void {
        if (uploadRef.current) {
            uploadRef.current.click();
        }
    }

    function clearActiveFileId(): void {
        setActiveFileId(undefined);
    }

    function handleFileDetailsToggle(id: string): void {
        if (id === activeFileId) {
            clearActiveFileId();
            return;
        }
        setActiveFileId(id);
    }

    async function upload(files: File[]): Promise<void> {
        if (!currentFolderId) {
            return;
        }
        setUploading(true);

        const uploads = [];
        try {
            if (files.length > fileLimit) {
                toastNotification('error', `You can't upload more than ${fileLimit} files.`);
                return;
            }

            for (const file of files) {
                const uploadPromise = createDocumentQuery.mutateAsync(
                    { file, parentFolderId: currentFolderId },
                    {
                        onSuccess: () => {
                            toastNotification('success', 'New file uploaded!');
                        },
                        onError: () => {
                            toastNotification('error', 'Unable to upload document.');
                        },
                    }
                );
                uploads.push(uploadPromise);
            }
            await Promise.allSettled(uploads);
        } finally {
            setUploading(false);
            worksheet && queryClient.invalidateQueries(['worksheets', startingFolderId]);
            otherSchedule && queryClient.invalidateQueries(['other-schedules', startingFolderId]);
        }
    }

    function handleClipboardConfirmClose(): void {
        setUploading(false);
        setIsClipboardImage(false);
        setClipboardFile(undefined);
    }

    function pasteClipboardFile(files: File[]): void {
        setClipboardFile(files[0]);
        if (files[0].type.includes('image')) {
            setIsClipboardImage(true);
        } else {
            upload(files);
        }
    }

    async function uploadClipboardFile(name: string): Promise<void> {
        if (!clipboardFile) {
            return;
        }
        const file = new File([clipboardFile], name, { type: clipboardFile.type });

        handleClipboardConfirmClose();
        await upload([file]);
        worksheet && queryClient.invalidateQueries(['worksheets', worksheet.id]);
        otherSchedule && queryClient.invalidateQueries(['other-schedules', otherSchedule.id]);
    }

    function replaceDocument(files: File[]): void {
        if (records.toReplace?.recordType === FileRecordTypeEnum.DOCUMENT) {
            if (files[0]) {
                setReplacementDocument(files[0]);
            }
        }
    }

    function resetTableSelections(): void {
        dispatch(_act(undefined));
        gridRef?.current?.clear();
        worksheet && queryClient.invalidateQueries(['worksheets', worksheet.id]);
        otherSchedule && queryClient.invalidateQueries(['other-schedules', otherSchedule.id]);
    }

    function viewInFYI(): void {
        const record = records.toViewInFYI;
        const id = record?.recordType === FileRecordTypeEnum.FYI_DOCUMENT ? record.metadata?.id : record?.source?.metadata?.id;
        if (id) {
            window.open(`${FYI_URL}/documents/${id}/preview`, '_blank');
        } else {
            toastNotification('error', 'Unable to open in fyi.');
        }
    }

    function handleReplaceClick(record: FileRecordNew): void {
        if (record.recordType === FileRecordTypeEnum.DOCUMENT) {
            if (replaceRef.current) {
                replaceRef.current.click();
            }
        } else {
            dispatch(_act('REPLACE_LINK'));
        }
    }

    async function handleGoToOriginal(record: FileRecordNew): Promise<void> {
        if (!record.source) {
            return;
        }
        handleFolderChange(record.source.path[record.source?.path.length - 2].id, record.source.id);
    }

    async function resyncFYI(): Promise<void> {
        const ids = records.toResyncFYI.map((r) => r.source?.id || r.id);

        try {
            await syncFYIQuery.mutateAsync({ ids });
        } catch (error) {
            toastNotification('error', 'Unable to re-sync from FYI.');
        }
    }

    function redirectToLocation(): void {
        if (!currentPath) {
            return;
        }

        const currentContext = currentPath[currentPath.length - 1].context;
        const redirectPath = getRedirectPath(currentPath, currentFolderId, currentContext);

        if (redirectPath !== undefined) {
            navigate(`/${shortId}${redirectPath}`);
            handleFolderChange(currentFolderId);
        }
    }

    return (
        <div>
            <DocumentBrowser locationSaveKey="file-browser-pos" />

            {/* REPLACE DOCUMENT CONFIRMATION */}
            {replacementDocument && records.toReplace && (
                <DocumentReplaceConfirm
                    replacementDocument={replacementDocument}
                    currentRecord={records.toReplace}
                    onClose={(): void => {
                        setReplacementDocument(undefined);
                        resetTableSelections();
                    }}
                />
            )}

            {/* DELETE RECORDS CONFIRMATION */}
            {_action === 'DELETE' && (
                <RecordDeleteConfirm
                    onClose={(): void => {
                        dispatch(_act(undefined));
                        worksheet && queryClient.invalidateQueries(['worksheets', worksheet.id]);
                        otherSchedule && queryClient.invalidateQueries(['other-schedules', otherSchedule.id]);
                    }}
                    fileRecords={records.toDelete}
                    onDelete={resetTableSelections}
                />
            )}

            {/* REPLACE LINK CONFIRMATION */}
            {_action === 'REPLACE_LINK' && records.toReplace && (
                <LinkReplaceConfirm fileRecord={records.toReplace} onClose={resetTableSelections} />
            )}

            {/* NEW MULTI-REFERENCE CONFIRMATION */}
            {_action === 'NEW_REFERENCES' && records.toMultiReference && (
                <NewReferencesWindow
                    workpaperId={workpaperId}
                    fileRecord={records.toMultiReference}
                    initialFolderId={currentFolderId}
                    onClose={resetTableSelections}
                />
            )}

            {/* MOVE RECORDS WINDOW */}
            {_action === 'MOVE' && (
                <MoveFileWindow
                    initialFolderId={currentFolderId}
                    workpaperId={workpaperId}
                    fileRecords={records.toMove}
                    onClose={resetTableSelections}
                />
            )}

            {/* CHECK IN RECORDS CONFIRMATION */}
            {_action === 'CHECK_IN' && <DocumentClearCheckOutConfirm fileRecords={records.toCheckIn} onClose={resetTableSelections} />}

            {/* RENAME RECORD CONFIRMATION */}
            {_action === 'RENAME' && records.toRename && (
                <FileRecordRenameConfirm fileRecord={records.toRename} onClose={resetTableSelections} />
            )}

            {/* DOWNLOAD RECORDS CONFIRMATION */}
            {_action === 'DOWNLOAD' && <DocumentDownloadConfirm fileRecords={records.toDownload} onClose={resetTableSelections} />}

            {/* DUPLICATE RECORDS CONFIRMATION */}
            {_action === 'DUPLICATE' && <FileDuplicateConfirm fileRecords={records.toDuplicate} onClose={resetTableSelections} />}

            {/* MAKE ORIGINAL CONFIRMATION */}
            {_action === 'MAKE_ORIGINAL' && (
                <MakeReferenceOriginalConfirm fileRecords={records.toMakeOriginal} onClose={resetTableSelections} />
            )}

            {/* NEW LINK CONFIRMATION */}
            {_action === 'NEW_LINK' && (
                <NewLinkDialog parentFolderId={currentFolderId} onClose={resetTableSelections} onCreate={resetTableSelections} />
            )}

            {/* PASTE IMAGE FROM CLIPBOARD */}
            {isClipboardImage && <ClipboardImageUploadConfirm onUpload={uploadClipboardFile} onClose={handleClipboardConfirmClose} />}

            <Dropzone onDrop={upload} disabled={disablePrimaryButton} onPaste={pasteClipboardFile} fileLimit={fileLimit}>
                <div className="pb-4">
                    {uploading && (
                        <div className="absolute top-0 left-0 h-full w-full bg-white/60 z-50 flex items-center justify-center">
                            <Spinner />
                        </div>
                    )}
                    <div>
                        {/* FILE INPUT */}
                        <FileInput innerRef={uploadRef} onSelect={upload} multiple />
                        <FileInput innerRef={replaceRef} onSelect={replaceDocument} />

                        <div className={`flex items-center flex-wrap gap-2 mb-8`}>
                            {title && <PageTitle title={title} />}

                            <section className="ml-auto">
                                {/* PRIMARY ACTIONS */}
                                <div className={`${records.count === 0 ? '' : 'hidden'}`}>
                                    <FilesPagePrimaryActions
                                        workpaperId={workpaperId}
                                        entityId={entityId}
                                        useSmallStyling={useSmallStyling}
                                        disablePrimaryButton={disablePrimaryButton}
                                        disableLocationButton={!currentPath || !currentPath[currentPath.length - 1].context}
                                        onFilter={setFilterQuery}
                                        onViewLocationClick={redirectToLocation}
                                        onNewFileClick={openFileInput}
                                        onNewLinkClick={(): void => {
                                            dispatch(_act('NEW_LINK'));
                                        }}
                                        onSearch={setSearchQuery}
                                        onSort={setSortQuery}
                                        searchRef={searchRef}
                                        defaultSearchValue={searchQuery}
                                    />
                                </div>

                                {/* SECONDARY ACTIONS */}
                                <SecondaryPageActions hidden={records.count === 0}>
                                    {/* FYI VIEW */}
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        label={`View in FYI`}
                                        onClick={viewInFYI}
                                        disabled={readOnly}
                                        hidden={records.toViewInFYI === undefined || !canAction}
                                    />
                                    {/* FYI Resync */}
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        label={`Re-sync FYI`}
                                        onClick={resyncFYI}
                                        disabled={readOnly || syncFYIQuery.isLoading}
                                        hidden={records.toResyncFYI.length === 0 || !canAction}
                                        loading={syncFYIQuery.isLoading}
                                    />
                                    {/* DOWNLOAD */}
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        label={`Download (${records.toDownload.length})`}
                                        onClick={(): void => {
                                            dispatch(_act('DOWNLOAD'));
                                        }}
                                        // disabled={readOnly && !isExternalUser}
                                        hidden={records.toDownload.length === 0}
                                    />
                                    {/* DUPLICATE */}
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        label={`Duplicate (${records.toDuplicate.length})`}
                                        onClick={(): void => {
                                            dispatch(_act('DUPLICATE'));
                                        }}
                                        disabled={readOnly}
                                        hidden={records.toDuplicate.length === 0 || !canAction}
                                    />
                                    {/* MAKE ORIGINAL */}
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        label={`Make Original (${records.toMakeOriginal.length})`}
                                        onClick={(): void => {
                                            dispatch(_act('MAKE_ORIGINAL'));
                                        }}
                                        disabled={readOnly}
                                        hidden={records.toMakeOriginal.length === 0 || !canAction}
                                    />
                                    {/* GO TO ORIGINAL FILE */}
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        label="Go to original"
                                        onClick={(): Promise<void> => handleGoToOriginal(records.toFindOriginal as FileRecordNew)}
                                        hidden={!records.toFindOriginal || !canAction}
                                    />
                                    {/* REPLACE A FILE */}
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        label={records?.toReplace?.status === FileStatusEnum.CHECKED_OUT ? 'Replace & Check In' : 'Replace'}
                                        onClick={(): void => handleReplaceClick(records.toReplace as FileRecordNew)}
                                        disabled={readOnly}
                                        hidden={!records.toReplace || !canAction}
                                    />
                                    {/* RENAME A FILE */}
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        label="Rename"
                                        onClick={(): void => {
                                            dispatch(_act('RENAME'));
                                        }}
                                        disabled={readOnly}
                                        hidden={!records.toRename || !canAction}
                                    />
                                    {/* MAKE REFERENCE FOR DOCUMENT OR LINK
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        label="New reference"
                                        onClick={(): void => {
                                            dispatch(_act('NEW_REFERENCE'));
                                        }}
                                        disabled={readOnly}
                                        hidden={!records.toReference || isExternalUser}
                                    /> */}
                                    {/* MAKE MULTIPLE REFERENCES FOR DOCUMENT OR LINK */}
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        label="New references"
                                        onClick={(): void => {
                                            dispatch(_act('NEW_REFERENCES'));
                                        }}
                                        disabled={readOnly}
                                        hidden={!records.toMultiReference || !canAction}
                                    />
                                    {/* MARK AS CHECKED_IN */}
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        label={`Mark as Checked In (${records.toCheckIn.length})`}
                                        onClick={(): void => {
                                            dispatch(_act('CHECK_IN'));
                                        }}
                                        disabled={readOnly}
                                        hidden={records.toCheckIn.length === 0 || !canAction}
                                    />
                                    {/* MOVE */}
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        label={`Move (${records.toMove.length})`}
                                        disabled={disableMove}
                                        onClick={(): void => {
                                            dispatch(_act('MOVE'));
                                        }}
                                        hidden={records.toMove.length === 0 || !canAction}
                                    />
                                    {/* DELETE */}
                                    <SecondaryPageActions.Button
                                        size={useSmallStyling ? 'xs' : 'sm'}
                                        type="danger"
                                        label={`Delete (${records.toDelete.length})`}
                                        onClick={(): void => {
                                            dispatch(_act('DELETE'));
                                        }}
                                        disabled={disableDelete}
                                        hidden={false || !canAction}
                                        collapsible={false}
                                    />
                                </SecondaryPageActions>
                            </section>
                        </div>
                        <div className="mt-4 grid" style={{ gridTemplateColumns: '1fr min-content' }}>
                            <section className={`${overlayInfoPanel && activeFileId ? '' : activeFileId ? 'border-r' : ''}`}>
                                <FilesTable
                                    readOnly={readOnly || !canAction}
                                    attachedFiles={worksheet?.files || otherSchedule?.files || undefined}
                                    restrictingFolderId={workpaperId}
                                    toResetFolderId={startingFolderId}
                                    initialFolderId={currentFolderId}
                                    gridRef={gridRef}
                                    filterQuery={filterQuery}
                                    sortQuery={sortQuery}
                                    searchQuery={searchQuery}
                                    isFlat={params.get('flat') === 'true'}
                                    onUploading={setUploading}
                                    onFolderChange={handleFolderChange}
                                    onDetailsToggle={handleFileDetailsToggle}
                                    onEmptyStateNewFile={openFileInput}
                                />
                            </section>

                            <div
                                className={`${
                                    overlayInfoPanel
                                        ? 'translate-x-0 absolute top-10 right-0 max-h-[540px]  bg-white h-full overflow-y-auto z-30'
                                        : 'sticky top-0'
                                } ${activeFileId ? 'w-[350px] border-l' : 'w-0'}`}
                                style={{ transition: overlayInfoPanel ? undefined : 'width 250ms' }}
                            >
                                {activeFileId && (
                                    <FileRecordDetails
                                        fileId={activeFileId}
                                        onClose={clearActiveFileId}
                                        readOnly={readOnly || !canAction}
                                        onGoToFolder={handleFolderChange}
                                        inContext={worksheet !== undefined || otherSchedule !== undefined}
                                    />
                                )}
                            </div>
                            {/* This section will overlay/slide out when a file is selected */}
                        </div>
                    </div>
                </div>
            </Dropzone>
        </div>
    );
}

export default FilesSection;
